// Pure placement maths for the tour card, kept free of the DOM so it can be
// unit tested. Rects are viewport-relative, as getBoundingClientRect returns.

const OPPOSITE = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
const MIRROR = { left: 'right', right: 'left', top: 'top', bottom: 'bottom' };

const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

const roomFor = (side, target, viewport) => ({
  top: target.top,
  bottom: viewport.height - (target.top + target.height),
  left: target.left,
  right: viewport.width - (target.left + target.width),
}[side]);

/**
 * @param {object} args
 * @param {{top,left,width,height}|null} args.target  spotlit rect, or null for a centred card
 * @param {{width,height}} args.card
 * @param {{width,height}} args.viewport
 * @param {'top'|'bottom'|'left'|'right'} [args.placement='bottom']  logical side, mirrored in RTL
 * @param {'ltr'|'rtl'} [args.dir='ltr']
 * @param {number} [args.gap=14]     distance between target and card
 * @param {number} [args.margin=12]  minimum distance from the viewport edge
 * @returns {{top:number,left:number,side:string|null,arrowOffset:number|null}}
 */
export function computePlacement({
  target, card, viewport, placement = 'bottom', dir = 'ltr', gap = 14, margin = 12,
}) {
  if (!target) {
    return {
      top: Math.max(margin, (viewport.height - card.height) / 2),
      left: Math.max(margin, (viewport.width - card.width) / 2),
      side: null,
      arrowOffset: null,
    };
  }

  const preferred = dir === 'rtl' ? MIRROR[placement] : placement;
  const order = [preferred, OPPOSITE[preferred], ...['bottom', 'right', 'top', 'left']]
    .filter((side, i, all) => all.indexOf(side) === i);

  const needed = (side) => (side === 'top' || side === 'bottom' ? card.height : card.width) + gap + margin;
  const side = order.find((s) => roomFor(s, target, viewport) >= needed(s))
    // Nothing fits cleanly: use whichever side has the most room.
    || [...order].sort((a, b) => roomFor(b, target, viewport) - roomFor(a, target, viewport))[0];

  const centerX = target.left + target.width / 2;
  const centerY = target.top + target.height / 2;
  const maxLeft = Math.max(margin, viewport.width - card.width - margin);
  const maxTop = Math.max(margin, viewport.height - card.height - margin);

  let top;
  let left;
  if (side === 'top' || side === 'bottom') {
    left = clamp(centerX - card.width / 2, margin, maxLeft);
    top = side === 'bottom' ? target.top + target.height + gap : target.top - gap - card.height;
    top = clamp(top, margin, maxTop);
  } else {
    top = clamp(centerY - card.height / 2, margin, maxTop);
    left = side === 'right' ? target.left + target.width + gap : target.left - gap - card.width;
    left = clamp(left, margin, maxLeft);
  }

  // Keep the arrow on the card's rounded body, pointing at the target centre.
  const arrowInset = 24;
  const arrowOffset = side === 'top' || side === 'bottom'
    ? clamp(centerX - left, arrowInset, card.width - arrowInset)
    : clamp(centerY - top, arrowInset, card.height - arrowInset);

  return { top, left, side, arrowOffset };
}
